import { CEO_PORTRAIT_URL } from '../config/cloudinaryTeamImages';
import missionIcon from '../assets/mission.svg';
import visionIcon from '../assets/vision.svg';
import approachIcon from '../assets/approach.svg';

/**
 * "About page" content — a singleton (unlike servicePages, there's only ever
 * one About page), editable the same way as TemplateServicePage: per-field
 * text/image/icon/link editing plus per-block style overrides and whole-
 * section show/hide. DEFAULT_PAGE_CONTENT below IS the page's original
 * hardcoded copy (AboutUs.jsx + WorkingWithUsSection.jsx + HowWeWorkSection.jsx),
 * so the CMS-driven page renders byte-identical to what shipped before.
 */
export const DEFAULT_PAGE_CONTENT = {
  hiddenSections: [],
  hero: {
    titleLine1: 'Meet our Founder & CEO',
    titleAccent: 'Mr. Jaffar Ali Chaudhary',
    paragraphs: [
      'Mr. Jaffar Ali founded Databiqs with a clear vision: to help founders and startups transform their ideas into revenue-ready AI and SaaS products without the typical delays, uncertainty, and technical complexity that slow down innovation.',
      'With deep expertise across AI development, full-stack engineering, and enterprise architecture, Jaffar has spent his career solving one of the hardest challenges in tech: how to go from concept to scalable product quickly, without compromising on quality or future growth potential.',
    ],
    ctaLabel: 'Book a Consultation',
    ctaTo: '/book-consultation',
    portraitSrc: CEO_PORTRAIT_URL,
    portraitAlt: 'Jaffar Ali, Founder & CEO',
  },
  stats: {
    items: [
      { text: 'TRUSTED BY 1,500+ CLIENTS' },
      { text: '100% CLIENT SATISFACTION' },
      { text: '12 YEARS OF INDUSTRY EXCELLENCE' },
      { text: '150+ SUCCESSFULLY DELIVERED PROJECTS' },
    ],
  },
  intro: {
    pill: 'About Us',
    titleLine: 'INNOVATING THE FUTURE OF',
    titleAccent: 'BUSINESS TECHNOLOGY',
    subtitle:
      "We're A Team Of Passionate Technologists Committed To Helping Businesses Unlock Their Full Potential Through Intelligent Automation And AI Solutions.",
    cards: [
      {
        imgSrc: missionIcon,
        imgAlt: 'Mission Icon',
        title: 'Our Mission',
        desc: 'To Democratize Access To Powerful AI And Automation Technologies Of Every Size To Make Smarter Decisions, Streamline Operations, And Scale With Confidence In A Rapidly Evolving Digital World.',
      },
      {
        imgSrc: visionIcon,
        imgAlt: 'Vision Icon',
        title: 'Our Vision',
        desc: 'To Become A Global Leader In AI Powered Business Transformation, Shaping A Future Where Intelligent Systems Enhance Productivity, Innovation, And Sustainable Growth Across All Industries.',
      },
      {
        imgSrc: approachIcon,
        imgAlt: 'Approach Icon',
        title: 'Our Approach',
        desc: 'We Design And Deploy AI Solutions That Are Practical, Ethical, And Scalable, Combining Data Intelligence, Automation, And Human Insight To Solve Real World Business Challenges And Deliver Measurable Impact.',
      },
    ],
  },
  workingWithUs: {
    pill: 'WORKING WITH US',
    title: 'What you can expect, every project',
    cards: [
      {
        icon: 'message',
        title: 'Clear communication',
        items: [
          'Weekly demo on a video call',
          'Written progress update every week',
          'We work in your tools: Slack, Jira, Notion',
        ],
      },
      {
        icon: 'lock',
        title: 'Security & confidentiality',
        items: [
          'NDA signed before you share anything',
          'Your data stays in your cloud',
          'Role-based access, logged changes',
        ],
      },
      {
        icon: 'shieldCheck',
        title: 'Quality you can check',
        items: [
          'Code reviews and automated tests',
          'Documentation with every handover',
          'Staging environment to review before launch',
        ],
      },
    ],
  },
  howWeWork: {
    pill: 'HOW WE WORK',
    title: 'From first call to production in four steps',
    ctaLabel: 'Book a Free 30-Min Consultation',
    ctaTo: '/book-consultation',
    steps: [
      { num: '01', badge: 'Week 1', title: 'Discovery', desc: 'We map goals, users, scope and risks, then send a fixed quote and delivery plan.' },
      { num: '02', badge: 'Weeks 2–3', title: 'Design', desc: 'Clickable wireframes and UI you review and approve before a line of code.' },
      { num: '03', badge: 'Weeks 4–10', title: 'Build', desc: 'Senior engineers ship in weekly sprints, with a live demo every Friday.' },
      { num: '04', badge: 'Ongoing', title: 'Launch & support', desc: 'We launch, monitor and hand over code, docs and accounts, all yours.' },
    ],
  },
  finalCta: {
    title: 'Ready to discuss your idea with our team?',
    subtitle:
      'If you are exploring AI automation, product strategy, or custom software, you can connect with us directly from this page and book a focused consultation session.',
    ctaLabel: 'Book a Consultation',
    ctaTo: '/book-consultation',
  },
};

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

export function createAboutPageTemplate() {
  return deepClone(DEFAULT_PAGE_CONTENT);
}

const mergeArrayOfObjects = (storedArr, tplArr) => {
  if (!Array.isArray(storedArr) || !storedArr.length) return tplArr;
  return storedArr.map((item, i) => ({ ...(tplArr[i] || tplArr[0] || {}), ...(item || {}) }));
};

/** Deep-merges stored (possibly partial/older) page data over the current defaults so the renderer never crashes. */
export function normalizeAboutPageData(stored) {
  const tpl = createAboutPageTemplate();
  if (!stored || typeof stored !== 'object') return tpl;

  return {
    hiddenSections: Array.isArray(stored.hiddenSections) ? stored.hiddenSections : tpl.hiddenSections,
    hero: {
      ...tpl.hero,
      ...(stored.hero || {}),
      paragraphs: Array.isArray(stored.hero?.paragraphs) ? stored.hero.paragraphs : tpl.hero.paragraphs,
    },
    stats: {
      ...tpl.stats,
      items: mergeArrayOfObjects(stored.stats?.items, tpl.stats.items),
    },
    intro: {
      ...tpl.intro,
      ...(stored.intro || {}),
      cards: mergeArrayOfObjects(stored.intro?.cards, tpl.intro.cards),
    },
    workingWithUs: {
      ...tpl.workingWithUs,
      ...(stored.workingWithUs || {}),
      cards: mergeArrayOfObjects(stored.workingWithUs?.cards, tpl.workingWithUs.cards).map((card, i) => ({
        ...card,
        items: Array.isArray(card.items) ? card.items : tpl.workingWithUs.cards[i]?.items || [],
      })),
    },
    howWeWork: {
      ...tpl.howWeWork,
      ...(stored.howWeWork || {}),
      steps: mergeArrayOfObjects(stored.howWeWork?.steps, tpl.howWeWork.steps),
    },
    finalCta: { ...tpl.finalCta, ...(stored.finalCta || {}) },
  };
}

export function createDefaultAboutPageStyles() {
  return {};
}

export function normalizeAboutPageStyles(stored) {
  if (!stored || typeof stored !== 'object') return {};
  const out = {};
  for (const [blockId, val] of Object.entries(stored)) {
    if (!val || typeof val !== 'object') continue;
    out[blockId] = {
      desktop: val.desktop && typeof val.desktop === 'object' ? { ...val.desktop } : {},
      mobile: val.mobile && typeof val.mobile === 'object' ? { ...val.mobile } : {},
    };
  }
  return out;
}

/** Full normalized { data, styles } shape, the way it's stored under content.aboutPage. */
export function normalizeAboutPage(stored) {
  return {
    data: normalizeAboutPageData(stored?.data),
    styles: normalizeAboutPageStyles(stored?.styles),
    updatedAt: stored?.updatedAt || new Date().toISOString(),
  };
}
